import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { Tooltip } from 'antd';
import {
  currentMetricsMetaSelector,
  supportedSortedMetricsSelector,
} from '@store/common/selectors';
import { omitBy, isUndefined } from 'lodash';
import { selectedMetricSelector } from '@store/metricsChartContainer/selectors';
import { EVENT_BASED_PRESET_KEYS, NETWORK_BASED_PRESET_KEYS } from '@src/utils/presets/constants';
import MetricDropdown from './MetricDropdown';
import { setGlobalOrderByMetric } from '@store/trends/base/actions';
import { globalOrderBySelector } from '@store/trends/base/selectors';
import useSupportedSecondaryMetrics from '@src/hooks/metrics/useSupportedSecondaryMetrics';
import { isAppModule } from '@src/utils/constants';
import { StyledTrendLabel, StyledTrendSelector } from './styled';
import { errorDataAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { getPresetDefaultMetric } from '@src/utils/presets/utils';
import useGlobalExcludeMetricCategories from '@src/hooks/metrics/useGlobalExcludeMetricCategories';
import { useCurrentLimitation } from '@src/hooks/useCurrentLimitation';
import { useDrilldownMeta } from '@src/hooks/common/useDrilldownMeta';

const DIMENSION_ORDER_BY_TEXT = 'Order By';

// derived from the diag header component
// Secondary Metric List
const TrendSelector = () => {
  const dispatch = useDispatch();
  const errorData = useAtomValue(errorDataAtom);
  const primaryMetric = useSelector(selectedMetricSelector);
  const orderByMetric = useSelector(globalOrderBySelector);
  const globalExcludeCategories = useGlobalExcludeMetricCategories([]);
  const currentMetricsMeta = useSelector(currentMetricsMetaSelector);
  const metrics = { ...(useSelector(supportedSortedMetricsSelector) as object) };
  const { useCaseKey, flowId } = useDrilldownMeta();
  const limitation = useCurrentLimitation();

  let supportedMetrics: any = metrics;

  if (isAppModule && !NETWORK_BASED_PRESET_KEYS.includes(useCaseKey)) {
    Object.values(currentMetricsMeta).forEach((metric: any) => {
      if (
        metric.defaultSecondaryMetric &&
        !supportedMetrics[metric.defaultSecondaryMetric] &&
        currentMetricsMeta[metric.defaultSecondaryMetric]
      ) {
        if (
          !currentMetricsMeta[metric.defaultSecondaryMetric].secondaryOnly ||
          orderByMetric === metric.defaultSecondaryMetric
        ) {
          supportedMetrics[metric.defaultSecondaryMetric] =
            currentMetricsMeta[metric.defaultSecondaryMetric];
        }
      }
    });

    if (EVENT_BASED_PRESET_KEYS.includes(useCaseKey)) {
      supportedMetrics = Object.values(supportedMetrics).reduce((acc: any, metric: any) => {
        if (!metric.flowID) {
          acc[metric.name] = metric;
        }
        return acc;
      }, {});
    }
  }

  if (
    !supportedMetrics[primaryMetric] &&
    currentMetricsMeta[primaryMetric] &&
    !currentMetricsMeta[primaryMetric].defaultSecondaryMetric
  ) {
    supportedMetrics[primaryMetric] = currentMetricsMeta[primaryMetric];
  }

  const supportedSortedMetrics = useSupportedSecondaryMetrics({
    metrics: supportedMetrics,
    limitation,
    flowId,
    globalExcludeCategories,
  });

  useEffect(() => {
    if (errorData[orderByMetric]) {
      const defaultMetric = getPresetDefaultMetric(useCaseKey);
      dispatch(setGlobalOrderByMetric(defaultMetric));
    }
  }, [errorData, orderByMetric, useCaseKey, dispatch]);

  const applyDimensionSortByMetric = (metric: string) => {
    dispatch(setGlobalOrderByMetric(metric));
  };

  return (
    <StyledTrendSelector>
      <StyledTrendLabel>
        <Tooltip
          placement="topLeft"
          title="Displays the top 200 values in order of the selected metric. Use the sort and search feature to rearrange the displayed values."
        >
          {DIMENSION_ORDER_BY_TEXT}
        </Tooltip>
      </StyledTrendLabel>
      <MetricDropdown
        metrics={omitBy(supportedSortedMetrics, isUndefined)}
        selectedMetric={orderByMetric}
        onChange={applyDimensionSortByMetric}
        size="small"
      />
    </StyledTrendSelector>
  );
};

export default TrendSelector;
